/**
 * Click-through consent copy shown on the signup consent gate: the Data
 * Processing Addendum and the Terms of Service, plus the FERPA scope notice.
 *
 * Structured (not HTML) so it renders through tokens and can be translated.
 * Source outline: commercial-surface ToS/Privacy outline + DPA outline. The
 * authoritative version string comes from `GET /signup/v1`; the server records
 * the acceptance (timestamp, IP, user agent) against its own version.
 */

export interface LegalSection {
  heading: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
}

export interface LegalDocument {
  title: string;
  intro: string;
  sections: readonly LegalSection[];
  /**
   * When set, this document is a distinct legal instrument incorporated into
   * another (the DPA forms part of the ToS). On the public /terms page the
   * renderer uses it as an eyebrow label above a thick-bordered document
   * break, so readers see where the Terms end and the addendum begins —
   * important because both documents number their sections from 1.
   */
  dividerLabel?: string;
}

export const DPA_DOCUMENT: LegalDocument = {
  title: 'Data Processing Addendum',
  dividerLabel: 'Part of these Terms of Service',
  intro:
    'This addendum governs how Gridiron Tracker (the processor) handles data your program (the controller) enters into the platform. It forms part of the Terms of Service.',
  sections: [
    {
      heading: '1. Scope of processing',
      paragraphs: [
        'Gridiron Tracker processes football program data only: team and event records, player names, cohort (grade, year or squad), jersey numbers, positions, participation and play statistics, plus the accounts of the staff who enter and read them.',
      ],
    },
    {
      heading: '2. Data your program must not submit',
      paragraphs: [
        'The platform is not designed to hold FERPA-protected education records. Your program agrees not to enter, and Gridiron Tracker does not knowingly store:',
      ],
      bullets: [
        'date of birth or any other identifier not needed to distinguish players on a roster',
        'contact information for players or guardians',
        'guardian or family information',
        'medical, injury, training-room or treatment notes',
        'photographs or other images of players',
        'disciplinary records',
        'academic records, grades, eligibility determinations or transcripts',
      ],
    },
    {
      heading: '3. Tenant isolation and access',
      paragraphs: [
        'Each program is a separate tenant. Tenant scoping is enforced in the database with row-level security, and every request is authorised against the persona in the caller\u2019s token. Gridiron Tracker staff access tenant data only when your program opens a support request that requires it, and every such access is written to an append-only audit log.',
      ],
    },
    {
      heading: '4. Sub-processors',
      paragraphs: [
        'Sub-processors are limited to the infrastructure needed to run the service — hosting, object storage for video and imagery, transactional email and payment processing. The current list is published in the Trust section of the documentation, and your program is notified before a new sub-processor begins processing.',
      ],
    },
    {
      heading: '5. Security measures',
      bullets: [
        'encryption in transit (TLS 1.2+) and at rest',
        'Argon2id password hashing and mandatory MFA for admin and coaching personas',
        'least-privilege database roles; no shared administrative credentials',
        'append-only, tamper-evident audit log for security and compliance events',
        'automated backups with documented restore testing',
      ],
    },
    {
      heading: '6. Retention and deletion',
      paragraphs: [
        'Play and event data is retained for the history window included in your plan. A Team Admin can export program data at any time. On termination, tenant data is deleted or irreversibly anonymised within 30 days, except where retention is legally required.',
      ],
    },
    {
      heading: '7. Incident notification',
      paragraphs: [
        'Gridiron Tracker notifies the program\u2019s Team Admin contact without undue delay, and in any case within 72 hours of becoming aware of a personal-data breach affecting your tenant, with the facts known at that time and the remediation in progress.',
      ],
    },
    {
      heading: '8. Your responsibilities as controller',
      bullets: [
        'keep persona assignments current; remove staff who leave the program',
        'restrict Play Entry and Team Admin access to people authorised by your program',
        'ensure entering roster data is permitted by your school or district policy',
        'never use the platform to store the record types listed in section 2',
      ],
    },
  ],
};

/**
 * Public privacy policy (marketing footer → /privacy). Consistent with the
 * DPA above; written for prospects and Fanatic account holders rather than
 * for the controller/processor relationship the DPA formalises.
 */
export const PRIVACY_DOCUMENT: LegalDocument = {
  title: 'Privacy Policy',
  intro:
    'This policy explains what Gridiron Tracker collects, why, and what it will never collect. It applies to the marketing site, team portals and Fanatic accounts.',
  sections: [
    {
      heading: '1. What we collect',
      bullets: [
        'account data: name, email address and the persona your program assigns you',
        'program data your team enters: rosters (player name, cohort, jersey number, position), events, plays and statistics',
        'operational data: sign-in timestamps, audit-log entries for security-relevant actions, and anonymous connection-quality telemetry from live surfaces',
        'billing data: plan, invoices and payment status — card numbers are held by the payment processor, never by Gridiron Tracker',
      ],
    },
    {
      heading: '2. What we never collect',
      paragraphs: [
        'The platform is football program data only. It is not designed to hold FERPA-protected education records, and programs agree not to enter them:',
      ],
      bullets: [
        'no dates of birth, contact, guardian or family information for players',
        'no medical, injury or treatment notes',
        'no photographs of players, disciplinary records, grades or transcripts',
      ],
    },
    {
      heading: '3. How data is used',
      paragraphs: [
        'Data is used to provide the service to your program: rendering stats, powering live views, producing exports and sending the transactional email you request (invites, password resets, billing notices). Aggregate, de-identified usage is analysed for capacity planning and product improvement. Gridiron Tracker does not sell personal data and does not use it for advertising.',
      ],
    },
    {
      heading: '4. Cookies and local storage',
      paragraphs: [
        'The app uses cookies and browser storage strictly to keep you signed in, remember display preferences such as your theme, and queue offline play entries until connectivity returns. There are no third-party advertising or analytics trackers.',
      ],
    },
    {
      heading: '5. Who can see what',
      paragraphs: [
        'Each program is an isolated tenant, enforced with database row-level security. Within a tenant, access follows the persona your program assigns. Public fanatic-facing surfaces show only what a Team Admin chooses to publish. Gridiron Tracker staff access tenant data only for support your program requests, and every such access is audit-logged.',
      ],
    },
    {
      heading: '6. Retention, export and deletion',
      paragraphs: [
        'Program data is retained for the history window of your plan and can be exported by a Team Admin at any time. On account termination, tenant data is deleted or irreversibly anonymised within 30 days except where retention is legally required. Fanatic account holders can delete their account from account settings.',
      ],
    },
    {
      heading: '7. Sub-processors and security',
      paragraphs: [
        'Sub-processors are limited to hosting, object storage, transactional email and payment processing; the current list is published in the Trust section of the documentation. Data is encrypted in transit and at rest, passwords are hashed with Argon2id, and admin personas require MFA.',
      ],
    },
    {
      heading: '8. Contact and changes',
      paragraphs: [
        'Privacy questions and data requests go to the address published in the Help Center under Contact Support. Material changes to this policy are announced at least 30 days before they take effect.',
      ],
    },
  ],
};

export const TOS_DOCUMENT: LegalDocument = {
  title: 'Terms of Service',
  intro:
    // No UI mechanics in the copy itself: this text also renders on the public
    // /terms page, where there is no Accept button. The signup consent gate
    // explains its own scroll-to-the-end unlock beside the scroll region.
    'By creating a team portal you agree to these terms on behalf of your program. Read them in full before accepting during signup.',
  sections: [
    {
      heading: '1. The service',
      paragraphs: [
        'Gridiron Tracker is a subscription statistics platform for football programs. Features available to your program are determined by your plan and by any per-tenant entitlements your program has been granted.',
      ],
    },
    {
      heading: '2. Trial and billing',
      paragraphs: [
        'New programs start on a 30-day Varsity trial. No payment card is collected for the trial and nothing is charged. On day 30 the account moves to the Free tier automatically unless a Team Admin selects a paid plan. Paid plans are billed monthly in advance and are cancellable at any time; the plan remains active until the end of the paid period.',
      ],
    },
    {
      heading: '3. Accounts and acceptable use',
      bullets: [
        'one account per person; credentials are not to be shared, including in the coaches\u2019 box',
        'do not attempt to access another program\u2019s tenant, or probe the service for vulnerabilities outside a written engagement',
        'do not upload unlawful content, or video and imagery you do not have the right to use',
        'automated access is limited to the documented API and the rate limits published for your plan',
      ],
    },
    {
      heading: '4. Your data',
      paragraphs: [
        'Your program owns the data it enters. Gridiron Tracker holds it to provide the service, and uses it in aggregate, de-identified form only for capacity planning and product improvement. Your program can export its data at any time in CSV, including the Hudl-compatible export.',
      ],
    },
    {
      heading: '5. Availability and support',
      paragraphs: [
        'The service targets 99.9% monthly availability outside published maintenance windows, with live-game traffic prioritised. Support requests are triaged with in-game issues first. Planned maintenance is scheduled outside the Thursday-to-Saturday competition window wherever possible.',
      ],
    },
    {
      heading: '6. Suspension and termination',
      paragraphs: [
        'Either party may terminate with 30 days\u2019 notice. Gridiron Tracker may suspend an account immediately for non-payment after notice, or for use that threatens the security or availability of the platform for other programs.',
      ],
    },
    {
      heading: '7. Warranty, liability and changes',
      paragraphs: [
        'The service is provided without warranty of fitness for a particular coaching outcome. Liability is limited to the fees paid in the twelve months preceding a claim. Material changes to these terms are announced at least 30 days before they take effect, and continued use after that date constitutes acceptance.',
      ],
    },
    {
      heading: '8. Contact',
      paragraphs: [
        'Questions about these terms, the addendum or a data request go to your program\u2019s support contact, or to the address published in the Help Center under Contact Support.',
      ],
    },
  ],
};
